/**
 * cover-youtube-background.front.js
 * Looping and pause / play control for YouTube cover block backgrounds
 */
const YOUTUBE_ORIGIN = 'https://www.youtube-nocookie.com';
const STATE_ENDED = 0;
const STATE_PLAYING = 1;

// How long YouTube shows its own pause button, title and "More videos" after playback starts.
const INTRO_DURATION = 4500;

// Height of YouTube's title and "More videos" bars, cropped off by zooming in during the intro.
const OVERLAY_HEIGHT = 110;

export default class CoverYoutubeBackground {
    constructor() {
        this.settings = {
            covers: document.querySelectorAll('.wp-block-cover.has-youtube-background'),
            pausedClass: 'is-video-paused',
            introClass: 'is-video-intro',
            playingClass: 'is-video-playing',
            reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)')
        };
    }

    init() {
        this.settings.covers.forEach((cover) => this.bindUIActions(cover));
    }

    bindUIActions(cover) {
        const iframe = cover.querySelector('.wp-block-cover__youtube-background iframe');
        const toggle = cover.querySelector('.wp-block-cover__youtube-toggle');

        if (!iframe || !toggle) return;

        toggle.addEventListener('click', () => {
            this.setPaused(cover, iframe, toggle, !cover.classList.contains(this.settings.pausedClass));
        });

        let introTimer = null;

        // Set on load, loop and resume, YouTube's button doesn't show after buffering.
        cover.introPending = true;

        window.addEventListener('message', (event) => {
            if (event.origin !== YOUTUBE_ORIGIN || event.source !== iframe.contentWindow) return;

            let data;
            try {
                data = JSON.parse(event.data);
            } catch (e) {
                return;
            }

            const state = data.event === 'onStateChange' ? data.info : data.info?.playerState;

            // Show our button over YouTube's while it flashes up at the start of playback.
            if (state === STATE_PLAYING && cover.introPending) {
                cover.introPending = false;
                clearTimeout(introTimer);

                // Zoom in just enough to push YouTube's bars (which scale too) outside the cover.
                const usableHeight = Math.max(iframe.offsetHeight - OVERLAY_HEIGHT * 2, 1);
                const scale = Math.min(Math.max(cover.offsetHeight / usableHeight, 1), 2);
                cover.style.setProperty('--youtube-intro-scale', scale.toFixed(3));

                cover.classList.add(this.settings.introClass);
                introTimer = setTimeout(() => cover.classList.remove(this.settings.introClass), INTRO_DURATION);
            }

            // Only reveal the video once it's playing, so YouTube's loading spinner stays hidden.
            if (state === STATE_PLAYING) {
                cover.classList.add(this.settings.playingClass);
            }

            // Loop manually, YouTube's own loop needs a playlist which shows skip buttons.
            if (state === STATE_ENDED) {
                cover.introPending = true;
                this.command(iframe, 'seekTo', [0, true]);
                this.command(iframe, 'playVideo');
            }
        });

        const connect = () => {
            // Ask the player to send us its state changes.
            iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), YOUTUBE_ORIGIN);
            this.command(iframe, 'addEventListener', ['onStateChange']);

            // The video is hidden for reduced motion, so stop it using resources too.
            if (this.settings.reducedMotion.matches) this.setPaused(cover, iframe, toggle, true);
        };

        // The iframe may already have loaded before this runs.
        connect();
        iframe.addEventListener('load', connect);
    }

    command(iframe, func, args = []) {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), YOUTUBE_ORIGIN);
    }

    setPaused(cover, iframe, toggle, paused) {
        this.command(iframe, paused ? 'pauseVideo' : 'playVideo');

        cover.classList.toggle(this.settings.pausedClass, paused);

        // Hide the paused player (and its play button), it's shown again when playback resumes.
        if (paused) cover.classList.remove(this.settings.playingClass);
        else cover.introPending = true;

        toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
        toggle.querySelector('.screen-reader-text').textContent = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    }
}
